// src\components\Payroll\PartialSalaryFld\shared\usePartialSalary.js
import { useState, useCallback, useEffect } from "react";
import axios from '../../../../api/apiClient';
import { domain, token } from '../../../Security';
import { fetchDataEmployeeTypesEligibleForPartialSalary } from "../../../Personnel/EmployeesListFld/EmployeesListApi";

export const usePartialSalary = ({ user, showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [options, setOptions] = useState({
    employeeTypes: [],
  });

  const apiUrl = `${domain}/api/PartialSalary`;

  // ── Fetch main table data ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const enhanced = response.data.map((item) => ({
        ...item,
        rawAmount: item.amount,
        amount: item.amount ? `₱${parseFloat(item.amount).toFixed(2)}` : "—",
        employeeTypeDescription: item.employeeTypeDescription || "—",
        positionDescription: item.positionDescription || "—",
      }));

      setData(enhanced);
      setFilteredData(enhanced);
    } catch (error) {
      console.error("Error fetching Partial Salary data:", error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // ── Fetch dropdowns ───────────────────────────────────────────────────────
  const fetchDropdowns = useCallback(async () => {
    try {
      const result = await fetchDataEmployeeTypesEligibleForPartialSalary();
      setOptions({
        employeeTypes: result.map((t) => ({
          value: t.id,
          label: t.description,
        })),
      });
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, [fetchData, fetchDropdowns]);

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async (form, onSuccess) => {
    try {
      setRequestLoading(true);
      const payload = buildPayload(form);
      await axios.post(apiUrl, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Partial Salary created successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating Partial Salary:", error);
      const msg = error?.response?.data?.message || "Failed to create partial salary.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Update ────────────────────────────────────────────────────────────────
  const handleUpdate = async (form, recordId, onSuccess) => {
    try {
      setRequestLoading(true);
      const payload = buildPayload(form);
      await axios.put(`${apiUrl}/${recordId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Partial Salary updated successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating Partial Salary:", error);
      const msg = error?.response?.data?.message || "Failed to update partial salary.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Search filter ─────────────────────────────────────────────────────────
  const handleFilter = useCallback(
    (value) => {
      if (!value) { setData(filteredData); return; }
      const lower = value.toLowerCase();
      const res = filteredData.filter((f) =>
        f?.id?.toString().startsWith(lower) ||
        f?.amount?.toString().toLowerCase().includes(lower) ||
        f?.employeeTypeDescription?.toLowerCase().includes(lower) ||
        f?.positionDescription?.toLowerCase().includes(lower)
      );
      setData(res);
    },
    [filteredData]
  );

  // ── Payload builder ───────────────────────────────────────────────────────
  const buildPayload = (form) => ({
    amount: parseFloat(form.amount),
    employeeTypeId: form.employeeTypeId || null,
    positionId: form.positionId || null,
    isActive: form.isActive ?? true,
  });

  return {
    loading,
    requestLoading,
    data,
    options,
    fetchData,
    handleAdd,
    handleUpdate,
    handleFilter,
  };
};